import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { TranslationNamespaceEnum } from "src/lib/i18next/types.ts";
import { useExonnHubStore } from "src/admin/modules/b2b-gate-order/components/ExonnHub/useExonnHubStore.ts";
import { useExonnHubCatalogParams } from "src/admin/modules/b2b-gate-order/components/ExonnHub/hooks/useExonnHubCatalogParams.ts";
import { useExonnHubCatalog } from "src/admin/modules/b2b-gate-order/components/ExonnHub/hooks/useExonnHubCatalog.tsx";
import { keepPreviousData } from "@tanstack/react-query";
import * as styles from "./SubHeader.style.ts";
import { SortValue } from "src/admin/modules/b2b-gate-order/components/ExonnHub/ExonnHub.constants.ts";
import { Flex } from "src/components/Flex.tsx";
import { Toolbar } from "src/admin/modules/b2b-gate-order/components/ExonnHub/components/Toolbar/Toolbar.tsx";
import { DropdownMenu } from "src/components/DropdownMenu/DropdownMenu.tsx";
import { TextV2 } from "src/components/TextV2/TextV2.tsx";
import { PaginationV2 } from "src/components/PaginationV2/PaginationV2.tsx";
import { useIsDemoShop } from "src/hooks/use-is-demo-shop.ts";

export const SubHeader = observer(
  ({ onChangePage }: { onChangePage: (page: number) => void }) => {
    const { t } = useTranslation(TranslationNamespaceEnum.ExonnHubComponent);
    const store = useExonnHubStore();

    const isDemoShop = useIsDemoShop();

    const { page, pageSize, sort: sortValue } = store;
    const sortOptions = [
      {
        label: t("noSortingApplied"),
        value: "default",
        onSelect: () => {
          store.setSort(undefined);
          store.setPage(1);
        },
      },
      ...(isDemoShop
        ? []
        : [
            {
              label: t("sort.cheapFirst"),
              value: SortValue.CheapFirst,
              onSelect: () => {
                store.setSort(SortValue.CheapFirst);
                store.setPage(1);
              },
            },
            {
              label: t("sort.expensiveFirst"),
              value: SortValue.ExpensiveFirst,
              onSelect: () => {
                store.setSort(SortValue.ExpensiveFirst);
                store.setPage(1);
              },
            },
          ]),
    ];
    const selectSortOption = sortOptions.find(
      (option) => option.value === sortValue,
    );
    const listParams = useExonnHubCatalogParams();
    const { data: catalogData } = useExonnHubCatalog(listParams, {
      placeholderData: keepPreviousData,
    });

    const rowCount = catalogData?.meta?.total || 0;
    const fromPage = catalogData?.meta?.from || 0;
    const toPage = catalogData?.meta?.to || 0;
    const pageCount = catalogData?.meta?.per_page || 0;

    const changePage = (page: number) => {
      onChangePage(page);
      store.setPage(page);
    };
    return (
      <div css={styles.subHeader}>
        <Flex alignItems={"center"}>
          <Toolbar />
          <DropdownMenu
            value={selectSortOption?.value}
            items={sortOptions}
            sideOffset={0}
            contentAlign={"end"}
          >
            <DropdownMenu.Trigger triggerBtnVariant={"standard"}>
              {t("sortBy")}:
              <DropdownMenu.Label>
                {selectSortOption?.label || t("noSortingApplied")}
              </DropdownMenu.Label>
              <DropdownMenu.Arrow />
            </DropdownMenu.Trigger>
          </DropdownMenu>
        </Flex>

        {rowCount > 0 && (
          <Flex gap={10} alignItems={"center"}>
            {rowCount > 0 && (
              <>
                <TextV2>
                  {t("paginationMetaText", {
                    total: rowCount,
                    from: fromPage,
                    to: toPage,
                    count: pageCount,
                    ns: TranslationNamespaceEnum.DataGridComponent,
                  })}
                </TextV2>

                <PaginationV2
                  count={Math.ceil(rowCount / pageSize)}
                  page={page}
                  onChange={changePage}
                />
              </>
            )}
          </Flex>
        )}
      </div>
    );
  },
);
